
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";

const NAVY = "#0B1F3A";
const BLUE = "#1F4E79";
const LIGHT_BLUE = "#EAF1F7";
const PALE_BLUE = "#F5F8FB";
const BORDER = "#CBD5E1";
const TEXT = "#172033";
const MUTED = "#64748B";
const WHITE = "#FFFFFF";
const LIGHT_GRAY = "#F1F5F9";
const SUCCESS = "#166534";
const DANGER = "#991B1B";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#EEF2F6",
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 22,
    paddingRight: 22,
    color: TEXT,
  },

  payrollCard: {
    height: 375,
    backgroundColor: WHITE,
    borderWidth: 1,
    borderColor: BORDER,
    marginBottom: 12,
  },

  payrollCardLast: {
    height: 375,
    backgroundColor: WHITE,
    borderWidth: 1,
    borderColor: BORDER,
  },

  topBand: {
    height: 5,
    backgroundColor: NAVY,
  },

  header: {
    paddingTop: 8,
    paddingBottom: 7,
    paddingLeft: 11,
    paddingRight: 11,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  schoolBlock: {
    width: "64%",
  },

  schoolName: {
    fontSize: 12,
    fontWeight: "bold",
    color: NAVY,
  },

  schoolSubtitle: {
    marginTop: 2,
    fontSize: 6.5,
    color: MUTED,
  },

  documentBlock: {
    width: "33%",
    alignItems: "flex-end",
  },

  documentTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: BLUE,
  },

  documentType: {
    marginTop: 2,
    fontSize: 6,
    color: MUTED,
  },

  referenceBar: {
    backgroundColor: LIGHT_BLUE,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 9,
    paddingRight: 9,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  referenceText: {
    fontSize: 6.2,
    color: MUTED,
  },

  referenceValue: {
    fontWeight: "bold",
    color: NAVY,
  },

  statusBar: {
    marginTop: 7,
    marginLeft: 9,
    marginRight: 9,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    backgroundColor: PALE_BLUE,
    borderWidth: 1,
    borderColor: BORDER,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  statusLeft: {
    width: "65%",
  },

  statusLabel: {
    fontSize: 5.8,
    color: MUTED,
    textTransform: "uppercase",
  },

  statusText: {
    marginTop: 2,
    fontSize: 7.2,
    fontWeight: "bold",
    color: SUCCESS,
  },

  statusRight: {
    width: "33%",
    alignItems: "flex-end",
  },

  netLabel: {
    fontSize: 5.8,
    color: MUTED,
    textTransform: "uppercase",
  },

  netAmount: {
    marginTop: 1,
    fontSize: 11,
    fontWeight: "bold",
    color: NAVY,
  },

  section: {
    marginTop: 7,
    marginLeft: 9,
    marginRight: 9,
    borderWidth: 1,
    borderColor: BORDER,
  },

  sectionHeader: {
    backgroundColor: NAVY,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 7,
    paddingRight: 7,
  },

  sectionHeaderText: {
    color: WHITE,
    fontSize: 6.4,
    fontWeight: "bold",
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },

  employeeGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  employeeCell: {
    width: "33.333%",
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 6,
    paddingRight: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },

  employeeLabel: {
    fontSize: 5.4,
    color: MUTED,
    textTransform: "uppercase",
    marginBottom: 1,
  },

  employeeValue: {
    fontSize: 6.8,
    fontWeight: "bold",
    color: TEXT,
  },

  breakdown: {
    marginTop: 7,
    marginLeft: 9,
    marginRight: 9,
    borderWidth: 1,
    borderColor: BORDER,
  },

  breakdownHeader: {
    backgroundColor: NAVY,
    flexDirection: "row",
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 7,
    paddingRight: 7,
  },

  breakdownHeaderLeft: {
    width: "50%",
  },

  breakdownHeaderRight: {
    width: "50%",
  },

  breakdownHeaderText: {
    color: WHITE,
    fontSize: 6.2,
    fontWeight: "bold",
    textTransform: "uppercase",
  },

  breakdownColumns: {
    flexDirection: "row",
  },

  earningsColumn: {
    width: "50%",
    borderRightWidth: 1,
    borderRightColor: BORDER,
  },

  deductionsColumn: {
    width: "50%",
  },

  breakdownRow: {
    minHeight: 18,
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 7,
    paddingRight: 7,
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },

  breakdownRowAlt: {
    backgroundColor: "#FAFBFC",
  },

  breakdownLabel: {
    width: "62%",
    fontSize: 6,
    color: TEXT,
  },

  breakdownAmount: {
    width: "38%",
    fontSize: 6.2,
    fontWeight: "bold",
    color: TEXT,
    textAlign: "right",
  },

  deductionLabel: {
    color: "#475569",
  },

  deductionAmount: {
    color: DANGER,
  },

  // NEW: Salary deduction explanation
  deductionReasonBox: {
    marginTop: 6,
    marginLeft: 9,
    marginRight: 9,
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 7,
    paddingRight: 7,
    backgroundColor: "#FFF7ED",
    borderWidth: 1,
    borderColor: "#FED7AA",
    borderLeftWidth: 3,
    borderLeftColor: DANGER,
  },

  deductionReasonTitle: {
    fontSize: 6.2,
    fontWeight: "bold",
    color: DANGER,
    textTransform: "uppercase",
    marginBottom: 3,
  },

  deductionReasonText: {
    fontSize: 6.5,
    color: TEXT,
    lineHeight: 1.3,
  },

  totalBox: {
    marginTop: 7,
    marginLeft: 9,
    marginRight: 9,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: PALE_BLUE,
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },

  totalLabel: {
    fontSize: 6,
    color: MUTED,
  },

  totalValue: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: TEXT,
  },

  netRow: {
    backgroundColor: NAVY,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  netRowLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: WHITE,
  },

  netRowAmount: {
    fontSize: 10,
    fontWeight: "bold",
    color: WHITE,
  },

  authorization: {
    marginTop: 7,
    marginLeft: 9,
    marginRight: 9,
    borderWidth: 1,
    borderColor: BORDER,
  },

  authorizationHeader: {
    backgroundColor: LIGHT_GRAY,
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 7,
    paddingRight: 7,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  },

  authorizationHeaderText: {
    fontSize: 6,
    fontWeight: "bold",
    color: NAVY,
    textTransform: "uppercase",
  },

  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 5,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
  },

  signatureBox: {
    width: "43%",
  },

  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: "#475569",
    marginTop: 7,
    paddingTop: 2,
  },

  signatureLabel: {
    textAlign: "center",
    fontSize: 5.8,
    color: MUTED,
  },

  cardFooter: {
    marginTop: 5,
    marginLeft: 9,
    marginRight: 9,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  footerText: {
    fontSize: 5.3,
    color: MUTED,
  },

  footerStrong: {
    fontWeight: "bold",
    color: NAVY,
  },

  signingPage: {
    backgroundColor: "#EEF2F6",
    paddingTop: 22,
    paddingBottom: 22,
    paddingLeft: 25,
    paddingRight: 25,
  },

  signingContainer: {
    backgroundColor: WHITE,
    borderWidth: 1,
    borderColor: BORDER,
  },

  signingTopBand: {
    height: 7,
    backgroundColor: NAVY,
  },

  signingHeader: {
    paddingTop: 12,
    paddingBottom: 11,
    paddingLeft: 14,
    paddingRight: 14,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  signingSchool: {
    fontSize: 14,
    fontWeight: "bold",
    color: NAVY,
  },

  signingSubtitle: {
    marginTop: 3,
    fontSize: 7,
    color: MUTED,
  },

  signingTitleBlock: {
    alignItems: "flex-end",
  },

  signingTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: BLUE,
  },

  signingPeriod: {
    marginTop: 3,
    fontSize: 7,
    color: MUTED,
  },

  signingNotice: {
    marginLeft: 14,
    marginRight: 14,
    marginBottom: 11,
    padding: 8,
    backgroundColor: LIGHT_BLUE,
    borderWidth: 1,
    borderColor: BORDER,
  },

  signingNoticeText: {
    fontSize: 7,
    color: TEXT,
  },

  signingTable: {
    marginLeft: 14,
    marginRight: 14,
    borderWidth: 1,
    borderColor: BORDER,
  },

  signingTableHeader: {
    flexDirection: "row",
    backgroundColor: NAVY,
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 5,
    paddingRight: 5,
  },

  signingHeaderName: {
    width: "27%",
  },

  signingHeaderType: {
    width: "17%",
  },

  signingHeaderMoney: {
    width: "18.66%",
  },

  signingHeaderText: {
    color: WHITE,
    fontSize: 6.5,
    fontWeight: "bold",
    textAlign: "center",
  },

  signingRow: {
    flexDirection: "row",
    minHeight: 30,
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 5,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },

  signingRowAlt: {
    backgroundColor: "#FAFBFC",
  },

  signingName: {
    width: "27%",
  },

  signingType: {
    width: "17%",
  },

  signingMoney: {
    width: "18.66%",
  },

  signingCellText: {
    fontSize: 7,
    color: TEXT,
  },

  signingMoneyText: {
    fontSize: 7,
    color: TEXT,
    textAlign: "right",
  },

  signingTotals: {
    marginTop: 12,
    marginLeft: 14,
    marginRight: 14,
    padding: 9,
    backgroundColor: PALE_BLUE,
    borderWidth: 1,
    borderColor: BORDER,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  signingTotalBox: {
    width: "31%",
  },

  signingTotalLabel: {
    fontSize: 6.5,
    color: MUTED,
    textTransform: "uppercase",
  },

  signingTotalValue: {
    marginTop: 2,
    fontSize: 8.5,
    fontWeight: "bold",
    color: NAVY,
  },

  adminSignatures: {
    marginTop: 24,
    marginLeft: 14,
    marginRight: 14,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  adminSignatureBox: {
    width: "38%",
  },

  adminSignatureLine: {
    borderTopWidth: 1,
    borderTopColor: "#475569",
    paddingTop: 4,
    marginTop: 18,
  },

  adminSignatureText: {
    textAlign: "center",
    fontSize: 6.5,
    color: MUTED,
  },

  signingFooter: {
    marginTop: 18,
    marginLeft: 14,
    marginRight: 14,
    paddingTop: 7,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  signingFooterText: {
    fontSize: 6,
    color: MUTED,
  },
});

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => {
  return `$${numberValue(value).toFixed(2)}`;
};

const firstValue = (...values) => {
  for (const value of values) {
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  return "-";
};

const getEmployeeName = (payroll) => {
  return firstValue(
    payroll?.teacher?.full_name,
    payroll?.teacher?.name,
    payroll?.teacher_name,
    payroll?.support_staff_name,
    payroll?.employee_name,
    payroll?.staff_name
  );
};

const getEmployeeType = (payroll) => {
  return payroll?.teacher
    ? "Teaching Staff"
    : "Support Staff";
};

const getEmployeeId = (payroll) => {
  return firstValue(
    payroll?.teacher?.employee_id,
    payroll?.teacher?.staff_id,
    payroll?.employee_id,
    payroll?.staff_id,
    payroll?.teacher?.username,
    payroll?.username
  );
};

const getPosition = (payroll) => {
  return firstValue(
    payroll?.teacher?.position,
    payroll?.teacher?.designation,
    payroll?.position,
    payroll?.designation,
    payroll?.role
  );
};

const getDepartment = (payroll) => {
  return firstValue(
    payroll?.teacher?.department,
    payroll?.department
  );
};

const getBasicSalary = (payroll) => {
  return numberValue(
    firstValue(
      payroll?.basic_salary,
      payroll?.salary,
      payroll?.monthly_salary,
      0
    )
  );
};

const getBonus = (payroll) => {
  return numberValue(
    firstValue(
      payroll?.night_school_commission,
      payroll?.bonus,
      payroll?.bonus_amount,
      payroll?.commission,
      payroll?.compensation,
      0
    )
  );
};

const getAdvance = (payroll) => {
  return numberValue(
    firstValue(
      payroll?.salary_advance,
      payroll?.advance,
      payroll?.salaryAdvance,
      0
    )
  );
};

const getTax = (payroll) => {
  return numberValue(
    firstValue(
      payroll?.tax,
      payroll?.tax_deduction,
      payroll?.income_tax,
      0
    )
  );
};

const getSocialSecurityTax = (payroll) => {
  return numberValue(
    firstValue(
      payroll?.salary_deduction,
      0
    )
  );
};

// NEW: Retrieve the deduction explanation from the API.
const getSalaryDeductionReason = (payroll) => {
  const reason = payroll?.salary_deduction_reason;

  return typeof reason === "string"
    ? reason.trim()
    : "";
};

const getGrossSalary = (payroll) => {
  const explicitGross = Number(
    firstValue(
      payroll?.gross_salary,
      payroll?.gross_pay,
      payroll?.gross_amount,
      NaN
    )
  );

  if (Number.isFinite(explicitGross)) {
    return explicitGross;
  }

  return getBasicSalary(payroll) + getBonus(payroll);
};

const getTotalDeductions = (payroll) => {
  const explicitDeductions = Number(
    firstValue(
      payroll?.total_deductions,
      payroll?.deductions_total,
      NaN
    )
  );

  if (Number.isFinite(explicitDeductions)) {
    return explicitDeductions;
  }

  return (
    getAdvance(payroll) +
    getTax(payroll) +
    getSocialSecurityTax(payroll)
  );
};

const getNetSalary = (payroll) => {
  const explicitNet = Number(
    firstValue(
      payroll?.total_paid,
      payroll?.net_salary,
      payroll?.net_pay,
      payroll?.net_amount,
      NaN
    )
  );

  if (Number.isFinite(explicitNet)) {
    return explicitNet;
  }

  return (
    getGrossSalary(payroll) -
    getTotalDeductions(payroll)
  );
};

const getPayrollMonth = (
  payroll,
  fallbackMonth = ""
) => {
  return firstValue(
    payroll?.month,
    fallbackMonth,
    "Monthly"
  );
};

const PayrollStatement = ({
  payroll,
  index,
  month,
  academicYear,
  isLast,
}) => {
  const name = getEmployeeName(payroll);

  const payrollMonth = getPayrollMonth(
    payroll,
    month
  );

  const reference = firstValue(
    payroll?.reference_number,
    payroll?.transaction_reference,
    payroll?.payment_reference,
    `PAY-${academicYear || "YEAR"}-${String(
      index + 1
    ).padStart(4, "0")}`
  );

  const basicSalary = getBasicSalary(payroll);
  const bonus = getBonus(payroll);
  const advance = getAdvance(payroll);
  const tax = getTax(payroll);
  const socialsecuritytax =
    getSocialSecurityTax(payroll);
  const grossSalary = getGrossSalary(payroll);
  const totalDeductions =
    getTotalDeductions(payroll);
  const netSalary = getNetSalary(payroll);

  // NEW: Only show the reason when a deduction exists.
  const hasSalaryDeduction =
    socialsecuritytax > 0;

  const deductionReason =
    getSalaryDeductionReason(payroll) ||
    "Reason not provided";

  return (
    <View
      style={
        isLast
          ? styles.payrollCardLast
          : styles.payrollCard
      }
      wrap={false}
    >
      <View style={styles.topBand} />

      <View style={styles.header}>
        <View style={styles.schoolBlock}>
          <Text style={styles.schoolName}>
            Zion Praise Academy - CRM School System
          </Text>

          <Text style={styles.schoolSubtitle}>
            Official Employee Payroll & Salary Payment
            Record
          </Text>
        </View>

        <View style={styles.documentBlock}>
          <Text style={styles.documentTitle}>
            PAYROLL STATEMENT
          </Text>

          <Text style={styles.documentType}>
            MONTHLY SALARY RECORD
          </Text>
        </View>
      </View>

      <View style={styles.referenceBar}>
        <Text style={styles.referenceText}>
          PAYMENT REF:{" "}
          <Text style={styles.referenceValue}>
            {reference}
          </Text>
        </Text>

        <Text style={styles.referenceText}>
          STATEMENT:{" "}
          <Text style={styles.referenceValue}>
            {String(index + 1).padStart(4, "0")}
          </Text>
        </Text>
      </View>

      <View style={styles.statusBar}>
        <View style={styles.statusLeft}>
          <Text style={styles.statusLabel}>
            Payment Status
          </Text>

          <Text style={styles.statusText}>
            SALARY PAYMENT RECORDED
          </Text>
        </View>

        <View style={styles.statusRight}>
          <Text style={styles.netLabel}>
            Net Salary Paid
          </Text>

          <Text style={styles.netAmount}>
            {money(netSalary)}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderText}>
            Employee & Payroll Information
          </Text>
        </View>

        <View style={styles.employeeGrid}>
          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Employee Name
            </Text>

            <Text style={styles.employeeValue}>
              {name}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Payroll Month
            </Text>

            <Text style={styles.employeeValue}>
              {payrollMonth}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Academic Year
            </Text>

            <Text style={styles.employeeValue}>
              {firstValue(academicYear, "-")}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Employee Type
            </Text>

            <Text style={styles.employeeValue}>
              {getEmployeeType(payroll)}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Employee ID
            </Text>

            <Text style={styles.employeeValue}>
              {getEmployeeId(payroll)}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Position
            </Text>

            <Text style={styles.employeeValue}>
              {getPosition(payroll)}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Department
            </Text>

            <Text style={styles.employeeValue}>
              {getDepartment(payroll)}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Payment Reference
            </Text>

            <Text style={styles.employeeValue}>
              {reference}
            </Text>
          </View>

          <View style={styles.employeeCell}>
            <Text style={styles.employeeLabel}>
              Payment Status
            </Text>

            <Text style={styles.employeeValue}>
              Recorded
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.breakdown}>
        <View style={styles.breakdownHeader}>
          <View style={styles.breakdownHeaderLeft}>
            <Text style={styles.breakdownHeaderText}>
              Earnings
            </Text>
          </View>

          <View style={styles.breakdownHeaderRight}>
            <Text style={styles.breakdownHeaderText}>
              Deductions
            </Text>
          </View>
        </View>

        <View style={styles.breakdownColumns}>
          <View style={styles.earningsColumn}>
            <View style={styles.breakdownRow}>
              <Text style={styles.breakdownLabel}>
                Basic Salary
              </Text>

              <Text style={styles.breakdownAmount}>
                {money(basicSalary)}
              </Text>
            </View>

            <View
              style={[
                styles.breakdownRow,
                styles.breakdownRowAlt,
              ]}
            >
              <Text style={styles.breakdownLabel}>
                Bonus / Compensation
              </Text>

              <Text style={styles.breakdownAmount}>
                {money(bonus)}
              </Text>
            </View>

            <View style={styles.breakdownRow}>
              <Text style={styles.breakdownLabel}>
                Gross Salary
              </Text>

              <Text style={styles.breakdownAmount}>
                {money(grossSalary)}
              </Text>
            </View>
          </View>

          <View style={styles.deductionsColumn}>
            <View style={styles.breakdownRow}>
              <Text
                style={[
                  styles.breakdownLabel,
                  styles.deductionLabel,
                ]}
              >
                Salary Advance
              </Text>

              <Text
                style={[
                  styles.breakdownAmount,
                  styles.deductionAmount,
                ]}
              >
                {money(advance)}
              </Text>
            </View>

            <View
              style={[
                styles.breakdownRow,
                styles.breakdownRowAlt,
              ]}
            >
              <Text
                style={[
                  styles.breakdownLabel,
                  styles.deductionLabel,
                ]}
              >
                Tax / Statutory
              </Text>

              <Text
                style={[
                  styles.breakdownAmount,
                  styles.deductionAmount,
                ]}
              >
                {money(tax)}
              </Text>
            </View>

            <View style={styles.breakdownRow}>
              <Text
                style={[
                  styles.breakdownLabel,
                  styles.deductionLabel,
                ]}
              >
                Salary Deduction
              </Text>

              <Text
                style={[
                  styles.breakdownAmount,
                  styles.deductionAmount,
                ]}
              >
                {money(socialsecuritytax)}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* NEW: Display the reason for the salary deduction. */}
      {hasSalaryDeduction && (
        <View style={styles.deductionReasonBox}>
          <Text style={styles.deductionReasonTitle}>
            Reason for Salary Deduction
          </Text>

          <Text style={styles.deductionReasonText}>
            {deductionReason}
          </Text>
        </View>
      )}

      <View style={styles.totalBox}>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>
            GROSS SALARY
          </Text>

          <Text style={styles.totalValue}>
            {money(grossSalary)}
          </Text>
        </View>

        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>
            TOTAL DEDUCTIONS
          </Text>

          <Text style={styles.totalValue}>
            {money(totalDeductions)}
          </Text>
        </View>

        <View style={styles.netRow}>
          <Text style={styles.netRowLabel}>
            NET SALARY PAID
          </Text>

          <Text style={styles.netRowAmount}>
            {money(netSalary)}
          </Text>
        </View>
      </View>

      <View style={styles.authorization}>
        <View style={styles.authorizationHeader}>
          <Text style={styles.authorizationHeaderText}>
            Authorization & Payment Confirmation
          </Text>
        </View>

        <View style={styles.signatureContainer}>
          <View style={styles.signatureBox}>
            <View style={styles.signatureLine}>
              <Text style={styles.signatureLabel}>
                Employee Signature
              </Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <View style={styles.signatureLine}>
              <Text style={styles.signatureLabel}>
                Authorized Officer
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.cardFooter}>
        <Text style={styles.footerText}>
          <Text style={styles.footerStrong}>
            {payrollMonth}
          </Text>
          {" • "}
          Official monthly payroll record
        </Text>

        <Text style={styles.footerText}>
          <Text style={styles.footerStrong}>
            {reference}
          </Text>
        </Text>
      </View>
    </View>
  );
};

function PayrollSlipGenerationPDF({
  payrolls = [],
  month,
  academicYear,
}) {
  const safePayrolls = Array.isArray(payrolls)
    ? payrolls
    : [];

  const payrollMonths = [
    ...new Set(
      safePayrolls
        .map((payroll) => payroll?.month)
        .filter(
          (value) =>
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ""
        )
    ),
  ];

  const payrollMonthLabel =
    payrollMonths.length === 1
      ? payrollMonths[0]
      : payrollMonths.length > 1
        ? "Multiple Months"
        : firstValue(month, "Monthly");

  const totalPayroll = safePayrolls.reduce(
    (sum, payroll) =>
      sum + getNetSalary(payroll),
    0
  );

  const totalGrossPayroll = safePayrolls.reduce(
    (sum, payroll) =>
      sum + getGrossSalary(payroll),
    0
  );

  const totalDeductionsPayroll =
    safePayrolls.reduce(
      (sum, payroll) =>
        sum + getTotalDeductions(payroll),
      0
    );

  const payrollPairs = [];

  for (
    let i = 0;
    i < safePayrolls.length;
    i += 2
  ) {
    payrollPairs.push(
      safePayrolls.slice(i, i + 2)
    );
  }

  return (
    <Document
      title={`Monthly Payroll - ${payrollMonthLabel}${
        academicYear
          ? ` ${academicYear}`
          : ""
      }`}
      author="Zion Praise Academy - CRM School System"
      subject="Official Monthly Payroll Statement"
    >
      {payrollPairs.map(
        (pair, pageIndex) => {
          return (
            <Page
              key={`payroll-page-${pageIndex}`}
              size="A4"
              orientation="portrait"
              style={styles.page}
              wrap={false}
            >
              {pair.map(
                (
                  payroll,
                  pairIndex
                ) => {
                  const globalIndex =
                    pageIndex * 2 +
                    pairIndex;

                  return (
                    <PayrollStatement
                      key={
                        payroll?.id ||
                        `payroll-${globalIndex}`
                      }
                      payroll={payroll}
                      index={globalIndex}
                      month={month}
                      academicYear={academicYear}
                      isLast={
                        pairIndex === pair.length - 1
                      }
                    />
                  );
                }
              )}
            </Page>
          );
        }
      )}

      <Page
        size="A4"
        orientation="portrait"
        style={styles.signingPage}
        wrap={false}
      >
        <View style={styles.signingContainer}>
          <View style={styles.signingTopBand} />

          <View style={styles.signingHeader}>
            <View>
              <Text style={styles.signingSchool}>
                Zion Praise Academy - CRM School System
              </Text>

              <Text style={styles.signingSubtitle}>
                Official Monthly Payroll Administration
              </Text>
            </View>

            <View style={styles.signingTitleBlock}>
              <Text style={styles.signingTitle}>
                MONTHLY SALARY SIGNING SHEET
              </Text>

              <Text style={styles.signingPeriod}>
                PAYROLL MONTH:{" "}
                {payrollMonthLabel}
                {academicYear
                  ? ` • ${academicYear}`
                  : ""}
              </Text>
            </View>
          </View>

          <View style={styles.signingNotice}>
            <Text style={styles.signingNoticeText}>
              Monthly payroll summary for administrative
              verification and authorization. The detailed
              individual payroll statements are provided
              on the preceding pages.
            </Text>
          </View>

          <View style={styles.signingTable}>
            <View style={styles.signingTableHeader}>
              <View style={styles.signingHeaderName}>
                <Text style={styles.signingHeaderText}>
                  EMPLOYEE NAME
                </Text>
              </View>

              <View style={styles.signingHeaderType}>
                <Text style={styles.signingHeaderText}>
                  TYPE / ID
                </Text>
              </View>

              <View style={styles.signingHeaderMoney}>
                <Text style={styles.signingHeaderText}>
                  GROSS
                </Text>
              </View>

              <View style={styles.signingHeaderMoney}>
                <Text style={styles.signingHeaderText}>
                  DEDUCTIONS
                </Text>
              </View>

              <View style={styles.signingHeaderMoney}>
                <Text style={styles.signingHeaderText}>
                  NET PAID
                </Text>
              </View>
            </View>

            {safePayrolls.map(
              (payroll, index) => {
                const employeeName =
                  getEmployeeName(payroll);

                const employeeType =
                  getEmployeeType(payroll);

                const employeeId =
                  getEmployeeId(payroll);

                return (
                  <View
                    key={`signing-${
                      payroll?.id || index
                    }`}
                    style={[
                      styles.signingRow,
                      index % 2 === 1
                        ? styles.signingRowAlt
                        : {},
                    ]}
                  >
                    <View style={styles.signingName}>
                      <Text style={styles.signingCellText}>
                        {employeeName}
                      </Text>
                    </View>

                    <View style={styles.signingType}>
                      <Text style={styles.signingCellText}>
                        {employeeType}
                      </Text>

                      <Text style={styles.signingCellText}>
                        {employeeId}
                      </Text>
                    </View>

                    <View style={styles.signingMoney}>
                      <Text style={styles.signingMoneyText}>
                        {money(getGrossSalary(payroll))}
                      </Text>
                    </View>

                    <View style={styles.signingMoney}>
                      <Text style={styles.signingMoneyText}>
                        {money(getTotalDeductions(payroll))}
                      </Text>
                    </View>

                    <View style={styles.signingMoney}>
                      <Text style={styles.signingMoneyText}>
                        {money(getNetSalary(payroll))}
                      </Text>
                    </View>
                  </View>
                );
              }
            )}
          </View>

          <View style={styles.signingTotals}>
            <View style={styles.signingTotalBox}>
              <Text style={styles.signingTotalLabel}>
                Total Gross Payroll
              </Text>

              <Text style={styles.signingTotalValue}>
                {money(totalGrossPayroll)}
              </Text>
            </View>

            <View style={styles.signingTotalBox}>
              <Text style={styles.signingTotalLabel}>
                Total Deductions
              </Text>

              <Text style={styles.signingTotalValue}>
                {money(totalDeductionsPayroll)}
              </Text>
            </View>

            <View style={styles.signingTotalBox}>
              <Text style={styles.signingTotalLabel}>
                Total Net Payroll
              </Text>

              <Text style={styles.signingTotalValue}>
                {money(totalPayroll)}
              </Text>
            </View>
          </View>

          <View style={styles.adminSignatures}>
            <View style={styles.adminSignatureBox}>
              <View style={styles.adminSignatureLine}>
                <Text style={styles.adminSignatureText}>
                  Payroll Officer
                </Text>
              </View>
            </View>

            <View style={styles.adminSignatureBox}>
              <View style={styles.adminSignatureLine}>
                <Text style={styles.adminSignatureText}>
                  Authorized Officer
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.signingFooter}>
            <Text style={styles.signingFooterText}>
              Zion Praise Academy - CRM School System •
              Monthly Payroll
            </Text>

            <Text style={styles.signingFooterText}>
              {payrollMonthLabel}
              {academicYear
                ? ` • ${academicYear}`
                : ""}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default PayrollSlipGenerationPDF;
